import { trpc } from './trpc';
import { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { AppRouter } from '@tagadapay/trpc-types';
import { useToast } from '@/components/ui/use-toast';

type OutputRouter = inferRouterOutputs<AppRouter>;
type InputRouter = inferRouterInputs<AppRouter>;
export type OrderListOutput = OutputRouter['orders']['list'];
export type Order = OrderListOutput['orders'][number];
export type OrderDetail = OutputRouter['orders']['details'];
export type RefundOrderInput = InputRouter['orders']['refund'];
export function useOrdersList(params?: Parameters<typeof trpc.orders.list.useQuery>[0]) {
  return trpc.orders.list.useQuery(
    params || {
      sortBy: { field: 'orders.createdAt', direction: 'desc' },
      pagination: { page: 1, pageSize: 10 },
      filters: {},
    },
  );
}

type CountOrdersInput = InputRouter['orders']['count'];
export function useOrdersCount(filters?: CountOrdersInput['filters']) {
  return trpc.orders.count.useQuery({ filters });
}

export function useToBePaidCount() {
  return useOrdersCount({ 'orders.status': ['to_be_paid'], 'orders.excludeDrafts': true });
}

/** Skip the unsynced count when the account has no Shopify/Woo push target (#TAG-1870). */
export function useUnsyncedPaidCount(enabled = true) {
  return trpc.orders.count.useQuery(
    {
      filters: {
        'orders.status': ['paid'],
        'orders.externalOrderId.isNull': true,
        'orders.excludeDrafts': true,
      } as any,
    },
    { enabled },
  );
}

export function useStorefrontSyncCapability() {
  return trpc.integrations.storefrontSyncCapability.useQuery(undefined, {
    staleTime: 60_000,
  });
}

export function useOrderDetails(id: string) {
  return trpc.orders.details.useQuery({ id });
}

export function useRefundOrder() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.orders.refund.useMutation({
    // Money-moving mutation with no server-side idempotency (TAG-1247): a retried
    // 500 becomes a second refund attempt at the processor. TAG-1731.
    retry: false,
    onSuccess: (e) => {
      utils.orders.list.invalidate();
      utils.orders.details.invalidate();
      toast({
        title: 'Success',
        description: 'Order refunded successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useSyncOrderToProvider() {
  const utils = trpc.useUtils();
  const { toast } = useToast();
  return trpc.orders.syncToProvider.useMutation({
    onSuccess: () => {
      utils.orders.list.invalidate();
      utils.orders.details.invalidate();
      toast({
        title: 'Success',
        description: 'Order synced to provider successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteOrders() {
  const utils = trpc.useUtils();
  const { toast } = useToast();
  return (trpc.orders as any).delete.useMutation({
    onSuccess: (data: any) => {
      utils.orders.list.invalidate();
      toast({
        title: 'Success',
        description: `${data.deletedCount} order(s) deleted successfully`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export type UpdateNoteAttributesInput = {
  orderId: string;
  attributes: Record<string, string | number>;
  merge?: boolean;
};

export function useMarkOrderAsPaid() {
  const utils = trpc.useUtils();
  const { toast } = useToast();
  return trpc.orders.markAsPaid.useMutation({
    onSuccess: () => {
      utils.orders.list.invalidate();
      utils.orders.details.invalidate();
      toast({
        title: 'Success',
        description: 'Order marked as paid successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateNoteAttributes() {
  const utils = trpc.useUtils();
  const { toast } = useToast();
  return trpc.orders.updateNoteAttributes.useMutation({
    onSuccess: () => {
      utils.orders.details.invalidate();
      toast({
        title: 'Success',
        description: 'Note attributes updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useGetCustomerPaymentInstruments(orderId: string, enabled: boolean) {
  return trpc.orders.getCustomerPaymentInstruments.useQuery({ orderId }, { enabled });
}

export function useRetryPayment() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.orders.retryPayment.useMutation({
    onSuccess: () => {
      utils.orders.list.invalidate();
      utils.orders.details.invalidate();
      toast({
        title: 'Success',
        description: 'Payment processed successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useGetLatestSyncError(orderId: string, enabled: boolean) {
  return trpc.orders.getLatestSyncError.useQuery({ orderId }, { enabled });
}

export function useExportOrdersCsv() {
  const { toast } = useToast();
  return trpc.orders.exportCsv.useMutation({
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useEditOrderAddress() {
  const utils = trpc.useUtils();
  const { toast } = useToast();
  return trpc.orders.editAddress.useMutation({
    onSuccess: () => {
      utils.orders.details.invalidate();
      toast({
        title: 'Success',
        description: 'Address updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
